import { Button } from 'antd';
import { useState } from 'react';
import { updateOrderProductsInformation } from '../../../../../services/orders';
import {
  ISerialErrors,
  IShipmentData,
  IorderProductsData,
  IproductItemData,
  IreferencesByCountry
} from '../../../../../types/orders';
import { IOrganizationConfig } from '../../../../../types/product.configuration.types';
import { displayErrorNotification, displaySuccessNotification } from 'utils/displayNudosStandardNotifications';
import OrderCountry from './OrderCountry/OrderCountry';
import { AxiosError } from 'axios';
import useProductsInOrderState from 'state/useProductsInOrderState';
import NudosConfirmActionModal from 'components/NudosComponents/NudosConfirmActionModal/NudosConfirmActionModal';

import BordFloatIsland from 'components/BordFloatIsland/BordFloatIsland';
import useOrdersState from 'state/useOrdersState';
import { IQrCard } from 'components/BordDownloadQrModal/Components/QRcard/QRcard';
import BordDownloadQrModal from 'components/BordDownloadQrModal/BordDownloadQrModal';
import './Tab2Products.scss';

const Tab2Products = ({
  productsByReferenceList,
  organizationName,
  organizationId,
  savedOrganizationConfigs,
  updateWithoutLoaders,
  orderId,
  selectedTabId,
  orderStatus,
  shimpmentData
}: {
  productsByReferenceList: IorderProductsData;
  organizationName?: string;
  organizationId?: number;
  savedOrganizationConfigs: IOrganizationConfig[];
  updateWithoutLoaders: () => Promise<void>;
  orderId: string;
  selectedTabId: number;
  orderStatus?: string;
  shimpmentData?: IShipmentData[];
}) => {
  const { areRequiredNPMInputsFilled } = useProductsInOrderState();
  const { productsSelected, openDownloadModal, setOpenDownloadModal, setProductsSelected } = useOrdersState();
  const [hasDataChanged, setHasDataChanged] = useState(false);
  const [loading, setLoading] = useState(false);
  const [listOfProductsToUpdate, setListOfProductsToUpdate] = useState<IproductItemData[]>([]);
  const [serialErrorsList, setSerialErrorsList] = useState<ISerialErrors[]>();
  const [showNPMConfirmationModal, setShowNPMConfirmationModal] = useState(false);

  const qrList: IQrCard[] =
    productsSelected?.items?.map(item => ({
      qrData: {
        serial: item?.stockReservation?.stockProduct?.serial ?? item?.serial ?? '',
        orgName: organizationName ?? '',
        productId: String(item?.productId)
      }
    })) || [];

  const isButtonDisabled = !hasDataChanged || loading;

  const updateDate = async () => {
    const areRequiredNPMInputsFilledList = Array.from(areRequiredNPMInputsFilled.values());
    if (areRequiredNPMInputsFilledList.includes(false) && !showNPMConfirmationModal) {
      setShowNPMConfirmationModal(true);
      return;
    }

    const productData = listOfProductsToUpdate.map(
      ({ saleId, purchaseValue, providerId, provider, serial, providerPurchaseOrder, mpnId, mpn }) => ({
        saleId: Number(saleId),
        purchaseValue: Number(purchaseValue),
        providerId,
        mpnId,
        mpn,
        providerName: provider,
        providerPurchaseOrder,
        serial
      })
    );
    try {
      const response = await updateOrderProductsInformation(productData);
      if (response) {
        if (response && Array.isArray(response)) {
          displayErrorNotification();
          const serialErrors: ISerialErrors[] = response;
          if (serialErrors.length > 0) {
            setSerialErrorsList([...serialErrors]);
          }
        } else {
          displaySuccessNotification({ customJSXMessage: <>La información ha sido actualizada</> });
          setHasDataChanged(false);
          setShowNPMConfirmationModal(false);
          await updateWithoutLoaders?.();
        }
      }
    } catch (error) {
      const responseError = error as AxiosError;
      if (responseError?.response?.data?.response && Array.isArray(responseError?.response?.data?.response)) {
        const serialErrors: ISerialErrors[] = responseError?.response?.data?.response;
        if (serialErrors.length > 0) {
          setSerialErrorsList([...serialErrors]);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const changeErrorList = (list: ISerialErrors[]) => {
    setSerialErrorsList([...list]);
  };
  const updateListOfProductsToUpdate = (product: IproductItemData) => {
    setHasDataChanged(true);
    setListOfProductsToUpdate(prevList => {
      const index = prevList.findIndex(p => p.saleId === product.saleId);
      if (index !== -1) {
        const updatedList = [...prevList];
        updatedList[index] = product;
        return updatedList;
      }
      return [...prevList, product];
    });
  };

  const handleCloseCallback = () => {
    setProductsSelected(null);
    setOpenDownloadModal(false);
  };
  const handleOpenCallback = () => setOpenDownloadModal(true);

  const qrModal = (
    <BordDownloadQrModal
      downloadPercent={0}
      buttonProps={{
        isButtonDisabled: false,
        handleClick: handleCloseCallback
      }}
      closeModalCallback={handleCloseCallback}
      qrDataProps={qrList}
    />
  );

  return (
    <div className="tab2Products">
      {showNPMConfirmationModal && (
        <NudosConfirmActionModal
          firstButtonAction={() => setShowNPMConfirmationModal(false)}
          secondButtonAction={() => updateDate()}
          firstButtonText="No, cancelar"
          secondButtonText="Si, guardar información"
          modalText="Todavía no se han ingresado los MPN de todas las unidades Apple en esta orden  ¿seguro que deseas continuar?"
          customHeight="fit-content"
        />
      )}
      {!!qrList?.length && (
        <BordFloatIsland
          leftContentCount={qrList?.length}
          onClose={handleCloseCallback}
          onButtonClick={handleOpenCallback}
          modal={qrModal}
          isModalOpen={openDownloadModal}
        />
      )}
      <div className="productListForm">
        <div className="orderCountriesList">
          {Object.entries(productsByReferenceList).map(([countryCode, countryData], i) => {
            return (
              <OrderCountry
                key={`${countryCode}-${i}`}
                countryCode={countryCode}
                countryName={countryData?.name || 'Sin información del país'}
                countryData={countryData as unknown as IreferencesByCountry}
                setHasDataChanged={setHasDataChanged}
                isLast={i + 1 === Object.entries(productsByReferenceList).length}
                serialErrorsList={serialErrorsList}
                changeErrorList={changeErrorList}
                organizationName={organizationName}
                updateListOfProductsToUpdate={updateListOfProductsToUpdate}
                updateWithoutLoaders={updateWithoutLoaders}
                savedOrganizationConfigs={savedOrganizationConfigs}
                organizationId={organizationId}
                orderId={orderId}
                selectedTabId={selectedTabId}
                orderStatus={orderStatus}
                shimpmentData={shimpmentData}
              />
            );
          })}
        </div>
        <div className="formButtonContainer">
          <Button
            htmlType="submit"
            disabled={isButtonDisabled || (serialErrorsList && serialErrorsList?.length > 0)}
            onClick={updateDate}
          >
            {loading ? 'Cargando...' : 'Guardar'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Tab2Products;
